import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import cn from "classnames";
import { useTranslation } from "react-i18next";

import Button, { ButtonTemplate } from "components/Button/Button";
import { CrossIcon } from "components/Icons";

import styles from "./styles.module.scss";

type BtnProps = {
  text: string;
  template?: ButtonTemplate;
  onSubmit: (event: React.SyntheticEvent) => void;
  disabled?: boolean;
  isLoading?: boolean;
  dataTestid?: string;
};

export type Props = {
  secondaryButton?: BtnProps;
  primaryButton: BtnProps;
  onClose: (event: {}, reason?: "backdropClick" | "escapeKeyDown") => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  borderedFooter?: boolean;
  hideCancel?: boolean;
  hideFooter?: boolean;
  classes?: {
    root?: string;
    header?: string;
    title?: string;
    body?: string;
    footer?: string;
  };
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  testId?: string;
};

export const Modal = (props: React.PropsWithChildren<Props>) => {
  const {
    onClose,
    title,
    subtitle,
    children,
    footer,
    primaryButton,
    secondaryButton,
    classes = {},
    hideCancel,
    borderedFooter,
    maxWidth = "sm",
    testId,
    hideFooter,
  } = props;
  const { t } = useTranslation();

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      onClose={onClose}
      scroll="body"
      open
    >
      <form
        className={styles.wrapper}
        onSubmit={e => {
          e.preventDefault();
          primaryButton.onSubmit(e);
        }}
        data-testid={testId}
      >
        <div className={cn(styles.header, classes.header)}>
          <div className={styles.header__inner}>
            <div
              className={cn(styles.header__title, classes.title)}
              data-testid="dialog-title"
            >
              {title}
            </div>
            <div className={styles.header__subtitle}>{subtitle}</div>
          </div>
          <IconButton
            onClick={onClose}
            edge="start"
            size="small"
            className={styles.close}
          >
            <CrossIcon data-testid="close-modal-cross" />
          </IconButton>
        </div>
        <div className={cn(styles.body, classes.body)}>{children}</div>
        {footer}
        {!hideFooter && (
          <div
            className={cn(
              styles.footer,
              {
                [styles.borderedFooter]: borderedFooter,
              },
              classes.footer
            )}
          >
            {!!primaryButton && (
              <Button
                text={t(primaryButton.text)}
                isLoading={primaryButton.isLoading}
                disabled={primaryButton.disabled || primaryButton.isLoading}
                className={styles.submitBtn}
                template={primaryButton.template || ButtonTemplate.Primary}
                dataTestid={primaryButton.dataTestid ?? "submit-modal-btn"}
                onClick={primaryButton.onSubmit}
                id="submit"
                type="submit"
              />
            )}
            {!!secondaryButton && (
              <Button
                text={t(secondaryButton.text)}
                disabled={secondaryButton.disabled}
                isLoading={secondaryButton.isLoading}
                className={styles.submitBtn}
                template={secondaryButton.template || ButtonTemplate.Danger}
                dataTestid="submit-secondary-modal-btn"
                onClick={secondaryButton.onSubmit}
                id="submit-submit-secondary"
              />
            )}
            {!hideCancel && (
              <Button
                text={t("workspace.create_engine.cancel")}
                template={ButtonTemplate.Ghost}
                onClick={onClose}
                dataTestid="close-modal-btn"
                disabled={primaryButton.isLoading}
              />
            )}
          </div>
        )}
      </form>
    </Dialog>
  );
};
